import './IndicatorCard.css';

import { useState } from 'react';
import { Box, Card, CardContent, CardHeader } from '@mui/material';
import { IndicatorAutomatic, IndicatorMetricsAnType, IndicatorScope, IndicatorType } from './indicatorHelpers';
import FrameworkLogo from '../../b2b-frameworks/components/FrameworkLogo';
import { Delete, Edit, InfoOutlined, MoreVert, StackedLineChart, Straighten, Workspaces } from '@mui/icons-material';
import InfoOnHover from '../../b2b-common/InfoOnHover';
import DimensionValuesTable from './DimensionValuesTable';
import WeightedValuesTable from './WeightedValuesTable';
import { generateClassName } from '../../b2b-common/helpers';
import MoreButton from '../../b2b-common/MoreButton';
import ConfirmationDialog from '../../b2b-common/ConfirmationDialog';
import useB2BTranslation from '../../b2b-common/hooks/useB2BTranslation';
import IndicatorImpact from '../../b2b-frameworks/components/framework-impact/IndicatorImpact';

export const IndicatorCardCommonDetails = ({ indicator, showDescription=true }) => {
    const [showDesc, setShowDesc] = useState(false);

    return (
        <Box className='indicator-card-details' display={'flex'} flexDirection={'column'} gap={1}>
            <div className='indicator-card-name' style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <IndicatorScope indicator={indicator} />
                <IndicatorAutomatic indicator={indicator} />
                <span className='indicator-card-name-text-with-icon'>
                    <span className='indicator-card-name-text-inner'>{indicator.indicatorName}</span>
                    {indicator.description && showDescription && (
                        <span
                            className="indicator-card-info-icon-wrapper"
                            onMouseEnter={() => setShowDesc(true)}
                            onMouseLeave={() => setShowDesc(false)}
                        >
                            <InfoOutlined fontSize="small" className="indicator-card-info" style={{ cursor: 'pointer', position: 'static', marginLeft: 4, verticalAlign: 'middle', marginBottom: '1px' }} />
                            {showDesc && (
                                <Box
                                    className="indicator-card-description-tooltip"
                                    sx={{
                                        position: 'absolute',
                                        left: 24,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        backgroundColor: '#fff',
                                        border: '1px solid #ccc',
                                        borderRadius: 1,
                                        p: 1,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                        zIndex: 10,
                                        minWidth: 300,
                                        maxWidth: 450,
                                    }}
                                >
                                    {indicator.description}
                                </Box>
                            )}
                        </span>
                    )}
                </span>
            </div>
        </Box>
    );
};

export const QuantitativeIndicatorDetails = ({ indicator }) => {
    return (
        <div className='indicator-card-quantitative'>
            <div className='indicator-card-quantitative-measurement-name'>
                <Straighten />
                {indicator.quantitativeMeasurementUnitDTO.quantitativeMeasurementUnitName}
            </div>
            <div className='indicator-card-quantitative-measurement-unit'>
                {indicator.quantitativeMeasurementUnitDTO.quantitativeMeasurementUnit}
            </div>
        </div>
    );
};

export const QuantitativeDimensionalIndicatorDetails = ({ indicator }) => {
    return (
        <>
            <div className='indicator-card-dimensional'>
                <div className='indicator-card-quantitative'>
                    <div className='indicator-card-quantitative-measurement-name'>
                        <Straighten />
                        {indicator.quantitativeMeasurementUnitDTO.quantitativeMeasurementUnitName}
                    </div>
                    <div className='indicator-card-quantitative-measurement-unit'>
                        {indicator.quantitativeMeasurementUnitDTO.quantitativeMeasurementUnit}
                    </div>
                </div>
                <div className='indicator-card-dimensions'>
                    <div className='indicator-card-dimensions-type-name'>
                        <StackedLineChart />
                        {indicator.dimensionalType.dimensionTypeName}
                    </div>
                    <InfoOnHover>
                        <DimensionValuesTable dimensionValues={indicator.dimensionalType.dimensionValues} />
                    </InfoOnHover>
                </div>
            </div>
        </>
    );
};

export const QualitativeIndicatorDetails = ({ indicator }) => {
    return (
        <div className='indicator-card-qualitative'>
            <div className='indicator-card-qualitative-type-name'>
                <Workspaces />
                {indicator.qualitativeTypeDTO.qualitativeTypeName}
            </div>
            <InfoOnHover>
                <WeightedValuesTable qualitativeWeightedValues={indicator.qualitativeTypeDTO.weightedValues} />
            </InfoOnHover>
        </div>
    );
};

const IndicatorCard = ({ indicator, showMoreButton = false, onEdit, onDelete, className = null, ...params }) => {
    const { t } = useB2BTranslation('b2b-indicators');

    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const [showDescription, setShowDescription] = useState(false);

    const indicatorHasDescription = indicator.description && indicator.description.length > 0;

    return (
        <Card variant='outlined' className={generateClassName('indicator-card', '', className)}  {...params}>
            <CardHeader
                title={indicator.indicatorName}
                className='indicator-card-header'
            />
            {indicatorHasDescription && (
                <>
                    <InfoOutlined className='indicator-card-info'
                        onMouseEnter={() => setShowDescription(true)}
                        onMouseLeave={() => setShowDescription(false)}
                    />
                    {showDescription && (
                        <Box className='indicator-card-description'>
                            {indicator.description}
                        </Box>
                    )}
                </>
            )}

            <CardContent className='indicator-card-content'>
                <Box className='indicator-type'>
                    <IndicatorType indicator={indicator} />
                    {'  ('}
                    <IndicatorMetricsAnType indicator={indicator} />
                    {')'}
                </Box>
                <Box className='indicator-usage'>
                    {t('Usage')}: {indicator.areaIndicatorDTOs.length}
                </Box>
                <IndicatorImpact frameworkSuite={'generic'} frameworkId={2} indicator={indicator} className={'indicator-card-impact'} />
                <div className='indicator-card-fws-usage'>
                    {indicator.frameworksLevel1sDTOs && indicator.frameworksLevel1sDTOs.length > 0 && (
                        indicator.frameworksLevel1sDTOs.map(framework => {
                            return (
                                <FrameworkLogo key={framework.frameworkDTO.frameworkId} framework={framework} showBadge={false} showOverLay={false} />
                            );
                        })
                    )}
                </div>
                {/* <GenericTabs tabsConfig={tabsConfig} className={'indicator-card-tabs b2b-tabs-theme-light-01'} /> */}
            </CardContent>
            {showMoreButton && (
                <MoreButton icon={<MoreVert />}
                    buttons={
                        [
                            {
                                label: t('Edit'),
                                icon: <Edit />,
                                rolesAllowed: (indicator.indicatorScope === 'PRODUCT') ? ['ROLE_SUPER_ADMIN'] : ['ROLE_ADMIN'],
                                onClick: () => onEdit && onEdit(indicator),
                            },
                            {
                                label: t('Delete'),
                                icon: <Delete />,
                                rolesAllowed: (indicator.indicatorScope === 'PRODUCT') ? ['ROLE_SUPER_ADMIN'] : ['ROLE_ADMIN'],
                                onClick: () => setIsConfirmationOpen(true)
                            },
                            // {
                            //     label: t('Usage'),
                            //     icon: <IndicatorIcon indicator={indicator} />,
                            //     onClick: () => console.log('Usage')
                            // }
                        ]
                    }
                    dropPositionAlign='right'
                    rolesAllowed={['ADMIN', 'SUPER_ADMIN']}
                    className={'jb01-more-button'}
                />
            )}
            <ConfirmationDialog
                title={t('Indicator Delete Confirmation Request')}
                mainText={t('Are you sure you want to delete the selected indicator?')}
                open={isConfirmationOpen}
                onClose={() => setIsConfirmationOpen(false)}
                onConfirm={() => onDelete && onDelete(indicator)}
            />
        </Card>
    );
};

export default IndicatorCard;